import moment from 'moment'
import { useEffect, useState } from 'react'

import { type ICommonField } from '@/app/types/common'
import { type IGuideMarker, type IInsuredData } from '@/app/types/dmsTypes'
import { common } from '@/app/utils'

import { addInsuredData, type InputEvent } from '../../../helper'

interface IParams {
    type: string
    data?: IInsuredData
    markersList: IGuideMarker[]
}

export const useFields = (params: IParams) => {
    const { type, data, markersList } = params
    const _data: IInsuredData = data || addInsuredData

    const initialFName: ICommonField = {
        name: 'Фамилия пациента',
        value: _data.fname,
        error: null,
        isTouched: false,
        placeholder: 'Фамилия пациента'
    }

    const initialIName: ICommonField = {
        name: 'Имя пациента',
        value: _data.iname,
        error: null,
        isTouched: false,
        placeholder: 'Имя пациента'
    }

    const initialOName: ICommonField = {
        name: 'Отчество пациента',
        value: _data.oname,
        error: null,
        isTouched: false,
        placeholder: 'Отчество пациента'
    }

    const initialPolisNumber: ICommonField = {
        name: 'Номер страхового полиса',
        value: _data.polisNumber,
        error: null,
        isTouched: false,
        placeholder: 'Номер страхового полиса'
    }

    const initialBirthDate: ICommonField = {
        name: 'Дата рождения',
        value: _data.birthDate ? moment(_data.birthDate).utc().format('YYYY-MM-DDTHH:mm:ss.SSS') : null,
        error: null,
        isTouched: false,
        placeholder: 'Дата рождения'
    }

    const initialExpirationDate: ICommonField = {
        name: 'Дата окончания полиса',
        value: _data.expirationDate ? moment(_data.expirationDate).utc().format('YYYY-MM-DDTHH:mm:ss.SSS') : null,
        error: null,
        isTouched: false,
        placeholder: 'Дата окончания полиса'
    }

    const initialPolisStartDate: ICommonField = {
        name: 'Дата прикрепления',
        value: _data.polisStartedAt ? moment(_data.polisStartedAt).utc().format('YYYY-MM-DDTHH:mm:ss.SSS') : null,
        error: null,
        isTouched: false,
        placeholder: 'Дата прикрепления'
    }

    const initialPolisEndDate: ICommonField = {
        name: 'Дата открепления',
        value: _data.polisFinishedAt ? moment(_data.polisFinishedAt).utc().format('YYYY-MM-DDTHH:mm:ss.SSS') : null,
        error: null,
        isTouched: false,
        placeholder: 'Дата открепления'
    }

    // const initialInsuranceCompany: ICommonField = {
    //     name: 'Страховая компания',
    //     value: _data.insuranceCompany,
    //     error: null,
    //     isTouched: false,
    //     placeholder: 'Страховая компания'
    // }

    const initialInsuranceCompanyId: ICommonField = {
        name: 'Страховая компания',
        value: _data.incuranceCompanyId,
        error: null,
        isTouched: false,
        placeholder: 'Страховая компания должна быть выбрана из списка'
    }

    const initialAddress: ICommonField = {
        name: 'Адрес',
        value: _data.address || '',
        error: null,
        isTouched: false,
        placeholder: 'Адрес'
    }

    const initialInsurer: ICommonField = {
        name: 'Наименование страхователя',
        value: _data.insurer || '',
        error: null,
        isTouched: false,
        placeholder: 'Страхователь'
    }

    const initialMarker: ICommonField = {
        name: 'Маркер идентификации',
        value:
            _data.identificationMarkers.length > 0
                ? _data.identificationMarkers.map((el) => el.code).join(',')
                : markersList.find((el) => el.sequence === 0)?.code || '#ffffff',
        error: null,
        isTouched: false,
        placeholder: 'Маркер идентификации'
    }

    const initialPhone: ICommonField = {
        name: 'Телефон',
        value: _data.phone,
        error: null,
        isTouched: false,
        placeholder: 'Укажите телефон'
    }

    const [fname, setFname] = useState<ICommonField>(initialFName)
    const [iname, setIname] = useState<ICommonField>(initialIName)
    const [oname, setOname] = useState<ICommonField>(initialOName)
    const [polisNumber, setPolisNumber] = useState<ICommonField>(initialPolisNumber)
    const [birthDate, setBirthDate] = useState<ICommonField>(initialBirthDate)
    const [expirationDate, setExpirationDate] = useState<ICommonField>(initialExpirationDate)
    const [polisStartedAt, setPolisStartedAt] = useState<ICommonField>(initialPolisStartDate)
    const [polisFinishedAt, setPolisFinishedAt] = useState<ICommonField>(initialPolisEndDate)
    const [insuranceCompanyId, setInsuranceCompanyId] = useState<ICommonField>(initialInsuranceCompanyId)
    const [address, setAddress] = useState<ICommonField>(initialAddress)
    const [insurer, setInsurer] = useState<ICommonField>(initialInsurer)
    const [marker, setMarker] = useState<ICommonField>(initialMarker)
    const [phone, setPhone] = useState<ICommonField>(initialPhone)

    const onChangeFname = (e: InputEvent) => setFname((prev) => ({ ...prev, value: e.target.value }))
    const onChangeIname = (e: InputEvent) => setIname((prev) => ({ ...prev, value: e.target.value }))
    const onChangeOname = (e: InputEvent) => setOname((prev) => ({ ...prev, value: e.target.value }))
    const onChangePolisNumber = (e: InputEvent) => setPolisNumber((prev) => ({ ...prev, value: e.target.value }))
    const onChangeBirthDate = (e: moment.Moment) =>
        setBirthDate((prev) => ({ ...prev, value: moment(e).format('YYYY-MM-DDTHH:mm:ss.SSS') }))
    const onChangeExpirationDate = (e: moment.Moment) =>
        setExpirationDate((prev) => ({ ...prev, value: moment(e).format('YYYY-MM-DDTHH:mm:ss.SSS') }))
    const onChangeStartDate = (e: moment.Moment) =>
        setPolisStartedAt((prev) => ({ ...prev, value: moment(e).format('YYYY-MM-DDT00:00:00.000') }))
    const onChangeEndDate = (e: moment.Moment) =>
        setPolisFinishedAt((prev) => ({ ...prev, value: moment(e).format('YYYY-MM-DDT23:59:59.000') }))
    const onChangeInsuranceCompanyId = (value: string | null) => setInsuranceCompanyId((prev) => ({ ...prev, value }))
    const onChangeAddress = (e: InputEvent) => setAddress((prev) => ({ ...prev, value: e.target.value }))
    const onChangeInsurer = (e: InputEvent) => setInsurer((prev) => ({ ...prev, value: e.target.value }))

    const onAddMarker = (color: string) =>
        setMarker((prev) => {
            if (!prev.value?.includes(color)) {
                return { ...prev, value: `${prev.value},${color}` }
            }
            return prev
        })
    const onRemoveMarker = (color: string) =>
        setMarker((prev) => {
            if (prev.value && prev.value.split(',').length > 1) {
                return {
                    ...prev,
                    value: prev.value
                        ? prev.value
                              .split(',')
                              .filter((el) => el !== color)
                              .join(',')
                        : ''
                }
            }
            return prev
        })

    const onChangePhone = (e: InputEvent) => setPhone((prev) => ({ ...prev, value: e.target.value }))
    const onSetIsTouchedFname = () => setFname((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedIname = () => setIname((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedOname = () => setOname((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedPolisNumber = () => setPolisNumber((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedBirthDate = () => setBirthDate((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedExpirationDate = () => setExpirationDate((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedInsuranceCompanyId = () => setInsuranceCompanyId((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedAddress = () => setAddress((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedInsurer = () => setInsurer((prev) => ({ ...prev, isTouched: true }))
    const onSetIsTouchedPhone = () => setPhone((prev) => ({ ...prev, isTouched: true }))

    useEffect(() => {
        if (!fname.value) {
            setFname((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else {
            setFname((prev) => ({ ...prev, error: null }))
        }

        if (!iname.value) {
            setIname((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else {
            setIname((prev) => ({ ...prev, error: null }))
        }

        if (!birthDate.value) {
            setBirthDate((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else {
            setBirthDate((prev) => ({ ...prev, error: null }))
        }

        if (!expirationDate.value) {
            setExpirationDate((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else {
            setExpirationDate((prev) => ({ ...prev, error: null }))
        }

        if (!polisNumber.value) {
            setPolisNumber((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else {
            setPolisNumber((prev) => ({ ...prev, error: null }))
        }

        if (!polisStartedAt.value) {
            setPolisStartedAt((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else {
            setPolisStartedAt((prev) => ({ ...prev, error: null }))
        }

        if (!insurer.value) {
            setInsurer((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else {
            setInsurer((prev) => ({ ...prev, error: null }))
        }

        if (type === 'add' && !insuranceCompanyId.value) {
            setInsuranceCompanyId((prev) => ({ ...prev, error: 'Обязательно выбрать из выпадающего списка' }))
        } else if (type === 'add' && insuranceCompanyId.value && insuranceCompanyId.value.length < 1) {
            setInsuranceCompanyId((prev) => ({ ...prev, error: 'Обязательно выбрать из выпадающего списка' }))
        } else {
            setInsuranceCompanyId((prev) => ({ ...prev, error: null }))
        }

        if (!phone.value) {
            setPhone((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else if (common.onGetPhoneLength(phone.value) < 11) {
            setPhone((prev) => ({ ...prev, error: 'Не заполнено' }))
        } else {
            setPhone((prev) => ({ ...prev, error: null }))
        }
    }, [
        fname.value,
        setFname,
        iname.value,
        setIname,
        birthDate.value,
        setBirthDate,
        expirationDate.value,
        setExpirationDate,
        polisNumber.value,
        setPolisNumber,
        polisStartedAt.value,
        setPolisStartedAt,
        insurer.value,
        setInsurer,
        insuranceCompanyId.value,
        setInsuranceCompanyId,
        phone.value,
        setPhone,
        type
    ])

    const onCheckIsToched = (): boolean => {
        if (
            fname.isTouched ||
            iname.isTouched ||
            oname.isTouched ||
            polisNumber.isTouched ||
            insuranceCompanyId.isTouched ||
            phone.isTouched
        ) {
            return true
        }
        return false
    }

    const onSetTouchAll = () => {
        onSetIsTouchedFname()
        onSetIsTouchedIname()
        onSetIsTouchedOname()
        onSetIsTouchedPolisNumber()
        onSetIsTouchedBirthDate()
        onSetIsTouchedExpirationDate()
        onSetIsTouchedInsuranceCompanyId()
        onSetIsTouchedAddress()
        onSetIsTouchedInsurer()
        onSetIsTouchedPhone()
    }

    return {
        iname,
        onChangeIname,
        onSetIsTouchedIname,
        //
        fname,
        onChangeFname,
        onSetIsTouchedFname,
        //
        oname,
        onChangeOname,
        onSetIsTouchedOname,
        //
        polisNumber,
        onChangePolisNumber,
        onSetIsTouchedPolisNumber,
        //
        birthDate,
        onChangeBirthDate,
        onSetIsTouchedBirthDate,
        //
        expirationDate,
        onChangeExpirationDate,
        onSetIsTouchedExpirationDate,
        //
        polisStartedAt,
        onChangeStartDate,
        //
        polisFinishedAt,
        onChangeEndDate,
        //
        insuranceCompanyId,
        onChangeInsuranceCompanyId,
        onSetIsTouchedInsuranceCompanyId,
        //
        address,
        onChangeAddress,
        onSetIsTouchedAddress,
        //
        insurer,
        onChangeInsurer,
        onSetIsTouchedInsurer,
        //
        marker,
        onAddMarker,
        onRemoveMarker,
        //
        phone,
        onChangePhone,
        onSetIsTouchedPhone,
        //
        contracts: _data.contracts,
        //
        isSomeToched: onCheckIsToched(),
        onSetTouchAll
    }
}
